'use client'

import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'

import type { Locale } from '@/i18n'
import SvgMagnifyingGlass from '@/icons/regular/MagnifyingGlass'
import SvgXmark from '@/icons/solid/Xmark'

import { AuthorSearchResultCard } from './author-search-result-card'
import { filterAuthorsByName } from './filter-authors'
import type { AuthorDirectorySearchEntry } from './types'

interface SearchLabels {
  clear: string
  label: string
  noResults: string
  placeholder: string
  result: string
  results: string
}

interface AuthorsDirectorySearchProps {
  authors: AuthorDirectorySearchEntry[]
  children: ReactNode
  heading: ReactNode
  labels: SearchLabels
  locale: Locale
}

export const AuthorsDirectorySearch = ({ authors, children, heading, labels, locale }: AuthorsDirectorySearchProps) => {
  const [query, setQuery] = useState('')
  const trimmedQuery = query.trim()
  const results = useMemo(() => filterAuthorsByName(authors, trimmedQuery, locale), [authors, locale, trimmedQuery])
  const isSearching = trimmedQuery.length > 0

  return (
    <>
      <div className="mb-7 grid gap-6 md:mb-9 md:grid-cols-[minmax(0,1fr)_20rem] md:items-end lg:grid-cols-[minmax(0,1fr)_22rem]">
        {heading}
        <div className="md:pb-1">
          <label htmlFor="authors-directory-search" className="sr-only">
            {labels.label}
          </label>
          <div className="border-navy/15 focus-within:border-lapis/50 focus-within:ring-lapis/15 relative flex min-h-12 items-center rounded-full border bg-white shadow-xs transition focus-within:ring-4">
            <SvgMagnifyingGlass aria-hidden className="text-navy/45 ml-4 h-4 w-4 shrink-0" />
            <input
              id="authors-directory-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={labels.placeholder}
              aria-controls="authors-directory-results"
              className="text-navy placeholder:text-navy/38 min-w-0 flex-1 bg-transparent py-3 pr-3 pl-3 text-sm font-medium outline-none [&::-webkit-search-cancel-button]:hidden"
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={labels.clear}
                data-tracking="ignore"
                className="text-navy/40 hover:text-navy focus-visible:ring-lapis mr-2 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
              >
                <SvgXmark aria-hidden className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {isSearching ? (
        <>
          <p role="status" className="text-navy/55 mb-5 text-sm font-medium">
            {results.length.toLocaleString(locale)} {results.length === 1 ? labels.result : labels.results}
          </p>
          {results.length > 0 ? (
            <div id="authors-directory-results" className="grid gap-4 sm:grid-cols-2">
              {results.map((author) => (
                <AuthorSearchResultCard key={author.id} author={author} />
              ))}
            </div>
          ) : (
            <div
              id="authors-directory-results"
              className="border-navy/10 rounded-3xl border bg-white/70 px-6 py-12 text-center"
            >
              <p className="text-navy/55 text-sm font-medium">{labels.noResults}</p>
            </div>
          )}
        </>
      ) : (
        <div id="authors-directory-results">{children}</div>
      )}
    </>
  )
}
